import React, { useContext, useEffect, useState } from "react";
import { Button, Col, Empty, Popconfirm, Row, Table, Tooltip } from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { get_language, translate } from "../../locale";
import { AdminContext } from "../../providers/AdminContext";
import { TableHelper } from "../../helpers/TableHelper";
import SubscriptionGroupModal from "../Modals/SubscriptionGroupModal";
import PropTypes from "prop-types";
import SubscriptionServices from "../../services/SubscriptionServices";
import SecondaryButton from "../Buttons/SecondaryButton";
import moment from "moment";

const SubscriptionGroups = ({ subscription, reload, onChange }) => {
  const [loading, setLoading] = useState(false);
  const [modalGroup, setModalGroup] = useState(false);
  const [groups, setGroups] = useState(null);
  const { user } = useContext(AdminContext);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 25,
      total: 0,
    },
  });
  const [total, setTotal] = useState(0);
  const [selectedGroup, setSelectedGroup] = useState(null);

  useEffect(() => {
    if (user !== null && user !== undefined) {
      setIsSuperAdmin(user.user.rol === "eira-super-admin");
    }
  }, [user]);

  useEffect(() => {
    if (subscription !== null && subscription.id !== undefined) {
      loadData();
    }
  }, [subscription, reload, JSON.stringify(tableParams)]);

  const loadData = async () => {
    if (subscription.id === undefined) return;
    setLoading(true);
    let data = {
      page_size: tableParams.pagination.pageSize,
      page_num: tableParams.pagination.current,
    };
    const sorter = TableHelper.sorter(tableParams);
    if (sorter !== null) {
      data = { ...data, order_by: sorter };
    }
    try {
      const body = {
        ...data,
        subscription_id: subscription.id,
      };
      const res = await SubscriptionServices.getSubscriptionGroups(body);
      if (res.status >= 200 && res.status < 300) {
        const groups = res.data.groups.map((pro) => ({
          id: pro.id,
          name: pro.name,
          start_date: pro.start_date,
          registration_end_date: pro.registration_end_date,
          users_limit: pro.users_limit,
          protocol_jump: pro.protocol.name,
        }));
        setGroups(groups);
        setTotal(res.data.total_pages * tableParams.pagination.pageSize);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelAdd = () => {
    setModalGroup(false);
    setSelectedGroup(null);
  };
  const handleCompleteAdd = (data) => {
    setModalGroup(false);
    setSelectedGroup(null);
    loadData();
  };

  const columns = [
    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      width: 150,
      render: (value, record, index) => (
        <div className="flex space-x-4 justify-center">
          <Popconfirm
            title={translate(
              "¿Está seguro de que quiere borrar este grupo de la suscripción?"
            )}
            okText={translate("Si")}
            cancelText={translate("No")}
            onConfirm={() => {
              deleteGroup(record.id);
            }}
            disabled={!isSuperAdmin}
          >
            <Tooltip title={translate("Eliminar")} color="blur">
              <Button
                type="text"
                icon={<DeleteOutlined />}
                size="small"
                className="border-none"
                disabled={!isSuperAdmin}
              />
            </Tooltip>
          </Popconfirm>
          <Tooltip title={translate("Editar")} color="blur">
            <Button
              type="text"
              icon={<EditOutlined />}
              size="small"
              className="border-none"
              onClick={() => setSelectedGroup(record)}
              disabled={!isSuperAdmin}
            />
          </Tooltip>
        </div>
      ),
    },
    {
      title: translate("ID"),
      dataIndex: "id",
      key: "id",
      sorter: (a, b) => {
        if (a.id < b.id) return -1;
        if (a.id > b.id) return 1;
        return 0;
      },
    },
    {
      title: translate("Nombre"),
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => {
        if (a.name < b.name) return -1;
        if (a.name > b.name) return 1;
        return 0;
      },
    },
    {
      title: translate("Fecha inicio"),
      dataIndex: "start_date",
      key: "start_date",
      render: (value) =>
        moment(value).format(
          get_language === "es" ? "DD/MM/YYYY" : "MM/DD/YYYY"
        ),
      sorter: (a, b) => {
        if (a.start_date < b.start_date) return -1;
        if (a.start_date > b.start_date) return 1;
        return 0;
      },
    },
    {
      title: translate("Fecha fin"),
      dataIndex: "registration_end_date",
      key: "registration_end_date",
      render: (value) =>
        moment(value).format(
          get_language === "es" ? "DD/MM/YYYY" : "MM/DD/YYYY"
        ),
      sorter: (a, b) => {
        if (a.registration_end_date < b.registration_end_date) return -1;
        if (a.registration_end_date > b.registration_end_date) return 1;
        return 0;
      },
    },
    {
      title: translate("Cupos"),
      dataIndex: "users_limit",
      key: "users_limit",
      sorter: (a, b) => {
        if (a.users_limit < b.users_limit) return -1;
        if (a.users_limit > b.users_limit) return 1;
        return 0;
      },
    },
    {
      title: translate("Saltar al protocolo"),
      dataIndex: "protocol_jump",
      key: "protocol_jump",
    },
  ];

  const deleteGroup = async (group_id) => {
    setLoading(true);
    try {
      const res = await SubscriptionServices.deleteSubscriptionGroup(group_id);
      if (res.status >= 200 && res.status < 300) {
        loadData();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      filters,
      sorter,
    });

    if (pagination.pageSize !== tableParams.pagination?.pageSize) {
      setGroups([]);
    }
  };

  return (
    <Row gutter={16}>
      <Col span="24" className="mb-6">
        <SecondaryButton
          disabled={!isSuperAdmin}
          onClick={() => setModalGroup(true)}
        >
          {translate("Agregar grupo")}
        </SecondaryButton>
        <SubscriptionGroupModal
          subscriptionId={Number(subscription?.id)}
          initialProtocol={subscription?.initial_protocol}
          visible={modalGroup || selectedGroup !== null}
          data={selectedGroup}
          onCancel={handleCancelAdd}
          onComplete={handleCompleteAdd}
        />
      </Col>
      <Col span="24">
        <Table
          rowKey={(i) => i.id}
          dataSource={groups}
          columns={columns}
          locale={{
            emptyText: <Empty description={translate("No hay datos")} />,
            sortTitle: translate("Ordenar"),
            triggerDesc: translate("Ordenar (descendente)"),
            triggerAsc: translate("Ordenar (ascendente)"),
            cancelSort: translate("Cancelar orden"),
          }}
          pagination={{ ...tableParams.pagination, total: total }}
          onChange={handleTableChange}
          loading={loading}
        />
      </Col>
    </Row>
  );
};
SubscriptionGroups.propTypes = {
  subscription: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    enterprise: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    initial_protocol: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    modules: PropTypes.array,
  }),
  reload: PropTypes.number,
  onChange: PropTypes.func,
};

export default SubscriptionGroups;
